/**
 * ClubEntry — Rezervasyonlar Modalı
 *
 * QR menüden gelen TicketReservations kayıtlarını kulüp girişi modundaki hızlı
 * satış ekranında listeler. Tarih aralığı (default: bugün-bugün), arama,
 * yenileme ve satır genişletme (misafirler + kalemler) destekler.
 *
 * Satır seviyesinde 3 nokta menüsünden "Giriş Yap" aksiyonu sunar:
 * PrePaymentCardScanModal yeniden kullanılarak kart okutma + (ayar açıksa)
 * deposito tahsilatı yapılır; ana satış ödemesi rezervasyonda alındığı için
 * atlanır. Backend assign-cards-from-reservation endpoint'i kart kayıtlarını
 * oluşturur ve rezervasyonu Consumed olarak işaretler.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import GroupsIcon from '@mui/icons-material/Groups';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LoginIcon from '@mui/icons-material/Login';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { tr as trLocale } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import {
  getTicketReservations,
  type TicketReservationRecord,
} from '../../../../../services/ticketReservationApi';
import {
  clubEntryService,
  type ClubEntryScreenSettingsDto,
  type AssignCardsFromReservationDto,
} from '../../../../../services/clubEntryService';
import { cardManagementService } from '../../../../../services/cardManagementService';
import PrePaymentCardScanModal, {
  type ScannedCardData,
  type DepositPaymentMethod,
} from './PrePaymentCardScanModal';

interface ClubEntryReservationsModalProps {
  open: boolean;
  onClose: () => void;
  /** Opsiyonel: belirli bir şube ile filtre. Geçilmezse tüm şirket kayıtları gelir. */
  branchId?: string | null;
  /** Aktif hızlı satış ekranı id'si. Giriş Yap akışında kart kaydına yazılır. */
  screenId?: number;
  /** ClubEntry ekran ayarları — depositEnabled ve cardMode için kullanılır. */
  clubEntrySettings?: ClubEntryScreenSettingsDto | null;
  /** Seçili müşteri (openSession için). Yoksa Genel Cari kullanılır. */
  selectedCustomer?: { id: string; companyName: string } | null;
  /** İşlemi yapan kullanıcı email'i (openSession.UserId). */
  currentUserEmail?: string;
  /** Nakit deposito için varsayılan kasa id. paymentOptions'tan ilk enabled cash. */
  defaultCashboxId?: number | null;
  /** POS deposito için varsayılan banka hesabı id. paymentOptions'tan ilk enabled pos. */
  defaultBankAccountId?: number | null;
  /**
   * POS+deposito>0 senaryosu için QuickSaleScreen tarafından sağlanır. Bu callback verilirse,
   * Giriş Yap akışında POS seçildiğinde modal kapatılır ve QuickSale'in normal POS flow'u
   * tetiklenir (sadece deposito sepete eklenir, bilet ücreti zaten online ödendiği için
   * eklenmez). POS başarılı olduktan sonra QuickSale tarafı backend'e
   * assignCardsFromReservation çağrısını DepositAlreadyCollected=true ile yapar.
   */
  onPosCheckInRequest?: (
    reservation: TicketReservationRecord,
    cards: ScannedCardData[],
    expectedCardCount: number
  ) => void;
}

const formatDateForApi = (d: Date | null): string | null => {
  if (!d || isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatDisplayDate = (iso?: string): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('tr-TR');
  } catch {
    return iso;
  }
};

const formatDisplayDateTime = (iso?: string): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('tr-TR');
  } catch {
    return iso;
  }
};

const formatCurrency = (n?: number): string => {
  const v = typeof n === 'number' ? n : 0;
  return v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';
};

const statusColor = (status?: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  if (!status) return 'default';
  const s = status.toLowerCase();
  if (s.includes('onay')) return 'success';
  if (s.includes('beklem') || s.includes('pending')) return 'warning';
  if (s.includes('red') || s.includes('iptal') || s.includes('cancel')) return 'error';
  return 'info';
};

const ClubEntryReservationsModal: React.FC<ClubEntryReservationsModalProps> = ({
  open,
  onClose,
  branchId,
  screenId,
  clubEntrySettings,
  selectedCustomer,
  currentUserEmail,
  defaultCashboxId,
  defaultBankAccountId,
  onPosCheckInRequest,
}) => {
  const { t } = useTranslation('salesSystem');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const today = useMemo(() => new Date(), []);
  const [fromDate, setFromDate] = useState<Date | null>(today);
  const [toDate, setToDate] = useState<Date | null>(today);
  const [search, setSearch] = useState<string>('');
  const [rows, setRows] = useState<TicketReservationRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  // Satır aksiyon menüsü + Giriş Yap akışı
  const [actionAnchor, setActionAnchor] = useState<HTMLElement | null>(null);
  const [actionRow, setActionRow] = useState<TicketReservationRecord | null>(null);
  const [showEntryCardModal, setShowEntryCardModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; type: 'success' | 'error' | 'info'; msg: string }>({
    open: false,
    type: 'success',
    msg: '',
  });

  const canCheckIn = useCallback((row: TicketReservationRecord | null): boolean => {
    if (!row) return false;
    if (!row.odemeBasarili) return false;
    const status = (row.siparisDurumu || '').toLowerCase();
    if (status.includes('consumed') || status.includes('kullan')) return false;
    if (status.includes('iptal') || status.includes('cancel')) return false;
    if (status.includes('red') || status.includes('reject')) return false;
    return true;
  }, []);

  const loadReservations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTicketReservations({
        branchId: branchId || undefined,
        fromDate: formatDateForApi(fromDate) || undefined,
        toDate: formatDateForApi(toDate) || undefined,
        search: search.trim() || undefined,
        page: 1,
        pageSize: 200,
      });
      setRows(data);
    } catch (err) {
      console.error('[ClubEntryReservationsModal] Yükleme hatası:', err);
      setError(t('clubEntryReservationsModal.errors.loadFailed'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [branchId, fromDate, toDate, search, t]);

  // Modal açıldığında veya tarih aralığı değişince otomatik yükle
  useEffect(() => {
    if (!open) return;
    loadReservations();
  }, [open, fromDate, toDate, loadReservations]);

  // Modal kapatılınca state'i sıfırla (tekrar açılınca bugün-bugün ile başlasın)
  useEffect(() => {
    if (open) return;
    setSearch('');
    setExpandedIds(new Set());
  }, [open]);

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      loadReservations();
    }
  };

  const handleOpenActionMenu = (event: React.MouseEvent<HTMLElement>, row: TicketReservationRecord) => {
    event.stopPropagation();
    setActionAnchor(event.currentTarget);
    setActionRow(row);
  };

  const handleCloseActionMenu = () => {
    setActionAnchor(null);
  };

  const handleStartEntry = () => {
    if (!actionRow) return;
    setActionAnchor(null);
    setShowEntryCardModal(true);
  };

  const handleEntryConfirm = useCallback(
    async (cards: ScannedCardData[], depositPaymentMethod: DepositPaymentMethod) => {
      if (!actionRow) return;

      const totalDeposit = cards.reduce((sum, c) => sum + (c.depositAmount || 0), 0);

      // POS + deposito > 0: Tahsilatı QuickSale'in normal POS flow'una devret. Modal'ı kapat,
      // QuickSaleScreen'e bildir; oradaki sepet → handlePosPaymentForQuickSale → CompleteSale
      // başarılı olduktan sonra parent assignCardsFromReservation({depositAlreadyCollected:true})
      // çağıracak. Bilet ücreti zaten online ödendi → sepete EKLENMEZ.
      if (
        depositPaymentMethod === 'POS' &&
        totalDeposit > 0 &&
        typeof onPosCheckInRequest === 'function'
      ) {
        const expectedCardCount =
          (actionRow.guestCount && actionRow.guestCount > 0)
            ? actionRow.guestCount
            : (actionRow.lineItems || []).reduce((s, li) => s + (li.miktar || 0), 0);
        setShowEntryCardModal(false);
        // ListEyi parent kapatabilir veya açık bırakabilir; biz parent'a devrediyoruz
        onPosCheckInRequest(actionRow, cards, expectedCardCount);
        setActionRow(null);
        return;
      }

      setSubmitting(true);
      try {
        const dto: AssignCardsFromReservationDto = {
          ticketReservationId: actionRow.id,
          screenId,
          depositPaymentMethod,
          depositCashboxId:
            depositPaymentMethod === 'CASH' && defaultCashboxId ? defaultCashboxId : undefined,
          depositBankAccountId:
            depositPaymentMethod === 'POS' && defaultBankAccountId ? defaultBankAccountId : undefined,
          cards: cards.map((c) => ({
            cardUID: c.cardUID,
            cardLabel: c.cardLabel,
            depositAmount: c.depositAmount || 0,
            serviceProductId: c.assignedServiceItem?.id,
            serviceProductName: c.assignedServiceItem?.name,
            entryMode: clubEntrySettings?.entryMode || 'SINGLE_ENTRY',
            totalEntryRights: c.entryRights || 1,
          })),
          // Nakit ya da deposito=0: backend kendi muhasebe kaydını yapacak
          depositAlreadyCollected: false,
        };

        await clubEntryService.assignCardsFromReservation(dto);

        // Her kart için openSession (sessizce devam et — mevcut paternin aynısı)
        for (const sc of cards) {
          if (!sc.cardId) continue;
          try {
            await cardManagementService.openSession({
              CardId: sc.cardId,
              AccountId: selectedCustomer?.id || '0',
              AccountName: selectedCustomer?.companyName || 'Genel Cari',
              UserId: currentUserEmail || '',
              ScreenId: screenId?.toString() || '',
            });
          } catch (sessErr) {
            console.warn('[ClubEntryReservationsModal] openSession hatası:', sessErr);
          }
        }

        setSnack({
          open: true,
          type: 'success',
          msg: t('clubEntryReservationsModal.entry.success', { count: cards.length }),
        });
        setShowEntryCardModal(false);
        setActionRow(null);
        await loadReservations();
      } catch (err: any) {
        const status = err?.response?.status;
        let msg = err?.response?.data?.message || t('clubEntryReservationsModal.entry.error');
        if (status === 409) msg = t('clubEntryReservationsModal.entry.alreadyConsumed');
        else if (status === 400 && err?.response?.data?.message) msg = err.response.data.message;
        setSnack({ open: true, type: 'error', msg });
      } finally {
        setSubmitting(false);
      }
    },
    [actionRow, screenId, clubEntrySettings, selectedCustomer, currentUserEmail, defaultCashboxId, defaultBankAccountId, loadReservations, t, onPosCheckInRequest],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { minHeight: isMobile ? '100vh' : '80vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <EventNoteIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="div">
            {t('clubEntryReservationsModal.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('clubEntryReservationsModal.subtitle')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Üst toolbar: tarih aralığı + arama + yenile */}
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mb: 2 }}
          >
            <DatePicker
              label={t('clubEntryReservationsModal.filters.fromDate')}
              value={fromDate}
              onChange={(d) => setFromDate(d)}
              format="dd.MM.yyyy"
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
            <DatePicker
              label={t('clubEntryReservationsModal.filters.toDate')}
              value={toDate}
              onChange={(d) => setToDate(d)}
              format="dd.MM.yyyy"
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
            <TextField
              size="small"
              placeholder={t('clubEntryReservationsModal.filters.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKey}
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={t('clubEntryReservationsModal.actions.refresh')}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadReservations}
                  disabled={loading}
                  size="small"
                >
                  {t('clubEntryReservationsModal.actions.refresh')}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </LocalizationProvider>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              {t('clubEntryReservationsModal.loading')}
            </Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <EventNoteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {t('clubEntryReservationsModal.empty.title')}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {t('clubEntryReservationsModal.empty.hint')}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('clubEntryReservationsModal.totalCount', { count: rows.length })}
            </Typography>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>{t('clubEntryReservationsModal.columns.reservationId')}</TableCell>
                    <TableCell>{t('clubEntryReservationsModal.columns.reservationDate')}</TableCell>
                    <TableCell>{t('clubEntryReservationsModal.columns.authorized')}</TableCell>
                    <TableCell align="right">{t('clubEntryReservationsModal.columns.guestCount')}</TableCell>
                    <TableCell align="right">{t('clubEntryReservationsModal.columns.amount')}</TableCell>
                    <TableCell>{t('clubEntryReservationsModal.columns.payment')}</TableCell>
                    <TableCell>{t('clubEntryReservationsModal.columns.status')}</TableCell>
                    <TableCell align="right" sx={{ width: 60 }}>
                      {t('clubEntryReservationsModal.columns.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const isExpanded = expandedIds.has(row.id);
                    return (
                      <React.Fragment key={row.id}>
                        <TableRow
                          hover
                          onClick={() => toggleExpand(row.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <IconButton size="small">
                              {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {row.siparisId}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDisplayDateTime(row.siparisOlusturmaTarihi)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDisplayDate(row.reservationDate)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.authorizedPerson?.fullName || row.kullaniciAdi}</Typography>
                            {row.authorizedPerson?.phone && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {row.authorizedPerson.phone}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              icon={<GroupsIcon sx={{ fontSize: 14 }} />}
                              label={row.guestCount ?? 0}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {formatCurrency(row.toplamTutar)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.odemeBasarili ? t('clubEntryReservationsModal.payment.paid') : (row.odemeDurumu || '-')}
                              size="small"
                              color={row.odemeBasarili ? 'success' : 'default'}
                              variant={row.odemeBasarili ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip label={row.siparisDurumu || '-'} size="small" color={statusColor(row.siparisDurumu)} />
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Tooltip title={t('clubEntryReservationsModal.actions.menuLabel')}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleOpenActionMenu(e, row)}
                                aria-label={t('clubEntryReservationsModal.actions.menuLabel')}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={9} sx={{ py: 0, border: 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 1, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                  {/* Misafirler */}
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <GroupsIcon fontSize="small" />
                                      {t('clubEntryReservationsModal.expanded.guests')}
                                    </Typography>
                                    {row.guests && row.guests.length > 0 ? (
                                      <Stack spacing={0.5}>
                                        {row.guests.map((g, idx) => (
                                          <Typography key={idx} variant="body2">
                                            {idx + 1}. {[g.firstName, g.lastName].filter(Boolean).join(' ') || '-'}
                                            {typeof g.age !== 'undefined' && ` (${g.age} ${t('clubEntryReservationsModal.expanded.ageUnit')})`}
                                            {g.type && ` — ${g.type}`}
                                          </Typography>
                                        ))}
                                      </Stack>
                                    ) : (
                                      <Typography variant="caption" color="text.disabled">
                                        {t('clubEntryReservationsModal.expanded.noGuests')}
                                      </Typography>
                                    )}
                                  </Box>

                                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

                                  {/* Kalemler */}
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <ShoppingCartIcon fontSize="small" />
                                      {t('clubEntryReservationsModal.expanded.items')}
                                    </Typography>
                                    {row.lineItems && row.lineItems.length > 0 ? (
                                      <Stack spacing={0.5}>
                                        {row.lineItems.map((it, idx) => (
                                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                            <Typography variant="body2">
                                              {it.urunAdi || '-'} <Typography component="span" variant="caption" color="text.secondary">x{it.miktar ?? 0}</Typography>
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                              {formatCurrency(it.toplamFiyat)}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Stack>
                                    ) : (
                                      <Typography variant="caption" color="text.disabled">
                                        {t('clubEntryReservationsModal.expanded.noItems')}
                                      </Typography>
                                    )}
                                  </Box>

                                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

                                  {/* İletişim */}
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      {t('clubEntryReservationsModal.expanded.contact')}
                                    </Typography>
                                    {row.authorizedPerson ? (
                                      <Stack spacing={0.5}>
                                        {row.authorizedPerson.email && (
                                          <Typography variant="body2">
                                            <strong>{t('clubEntryReservationsModal.expanded.email')}:</strong> {row.authorizedPerson.email}
                                          </Typography>
                                        )}
                                        {row.authorizedPerson.identityNumber && (
                                          <Typography variant="body2">
                                            <strong>{t('clubEntryReservationsModal.expanded.identityNumber')}:</strong> {row.authorizedPerson.identityNumber}
                                          </Typography>
                                        )}
                                        {row.authorizedPerson.address && (
                                          <Typography variant="body2">
                                            <strong>{t('clubEntryReservationsModal.expanded.address')}:</strong> {row.authorizedPerson.address}
                                          </Typography>
                                        )}
                                      </Stack>
                                    ) : (
                                      <Typography variant="caption" color="text.disabled">
                                        {t('clubEntryReservationsModal.expanded.noContact')}
                                      </Typography>
                                    )}
                                  </Box>
                                </Stack>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('clubEntryReservationsModal.actions.close')}</Button>
      </DialogActions>

      {/* Satır aksiyon menüsü */}
      <Menu
        anchorEl={actionAnchor}
        open={Boolean(actionAnchor)}
        onClose={handleCloseActionMenu}
      >
        <MenuItem onClick={handleStartEntry} disabled={!canCheckIn(actionRow) || submitting}>
          <ListItemIcon>
            <LoginIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('clubEntryReservationsModal.actions.checkIn')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Giriş Yap akışı: kart okutma + (ayar açıksa) deposito.
          Bilet ücreti QR menü üzerinden online tahsil edildiği için prepaidMode=true geçilir
          ve kart sayısı rezervasyondaki guestCount üzerinden belirlenir. */}
      {showEntryCardModal && actionRow && (
        <PrePaymentCardScanModal
          open
          onClose={() => {
            if (submitting) return;
            setShowEntryCardModal(false);
          }}
          onConfirm={handleEntryConfirm}
          cardMode={clubEntrySettings?.cardMode === 'FLEXIBLE' ? 'FLEXIBLE' : 'ONE_PER_RIGHT'}
          serviceItems={(actionRow.lineItems || []).map((li) => ({
            id: li.urunId || '',
            name: li.urunAdi || '',
            price: li.birimFiyat || 0,
            quantity: li.miktar || 1,
          }))}
          depositEnabled={clubEntrySettings?.depositEnabled || false}
          servicePaymentMethod={undefined}
          prepaidMode
          expectedCardCountOverride={
            (actionRow.guestCount && actionRow.guestCount > 0)
              ? actionRow.guestCount
              : (actionRow.lineItems || []).reduce((s, li) => s + (li.miktar || 0), 0)
          }
        />
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snack.type}
          variant="filled"
          onClose={() => setSnack((p) => ({ ...p, open: false }))}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ClubEntryReservationsModal;
